import type { ParsedUrlQuery } from "querystring";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getOrderDetailsV2, getPolicyStatus } from "@tm/insurance/api";
import { getPartnerOnClientSide } from "@tm/insurance/utils";
import type { OrderDetailsV2ApiResponse } from "../types/api";
import { normalizeOrderDetailsV2 } from "../utils/normalize";

// ── Types ───────────────────────────────────────────────────────────────

interface PollingConfig {
  isEnabled: boolean;
  intervalMs: number;
  durationMs?: number;
}

interface UsePolicyPollingOptions {
  /** Raw order data from the server/props */
  data: OrderDetailsV2ApiResponse;
  /** router.query from Next.js useRouter */
  routerQuery: ParsedUrlQuery;
}

interface UsePolicyPollingResult {
  /** Current order data (may have been updated by polling) */
  orderData: OrderDetailsV2ApiResponse;
  /** Setter for external updates (e.g. after endorsement save) */
  setOrderData: Dispatch<SetStateAction<OrderDetailsV2ApiResponse>>;
  /** Manually refetch the full order details */
  refreshOrderDetails: () => Promise<void>;
}

// ── Helper: extract cmp from query ──────────────────────────────────────

const extractCmp = (query: ParsedUrlQuery): string => {
  const { cmp } = query || {};
  return typeof cmp === "string" ? cmp : cmp?.[0] || "";
};

// ── Hook ────────────────────────────────────────────────────────────────

export function usePolicyPolling({
  data,
  routerQuery,
}: UsePolicyPollingOptions): UsePolicyPollingResult {
  const [orderData, setOrderData] = useState(data);

  // ── Derive stable initial values from the data prop ─────────────────

  const initialNormalized = useMemo(
    () => normalizeOrderDetailsV2(data),
    [data]
  );

  const initialBookingId = initialNormalized.bookingId;

  const initialPollingConfig = initialNormalized.pageData?.pollingConfig as
    | PollingConfig
    | undefined;

  const initialPolicyStatus =
    (initialNormalized.pageData?.policyGenerationStatus as
      | string
      | undefined) ?? null;

  // ── Refs for polling lifecycle ──────────────────────────────────────

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const pollingInitializedRef = useRef<string | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const initialStatusRef = useRef<string | null>(initialPolicyStatus);

  // ── Manual refresh ─────────────────────────────────────────────────

  const refreshOrderDetails = useCallback(async () => {
    const partner = getPartnerOnClientSide();
    const updatedOrderData = await getOrderDetailsV2({
      partner,
      bookingId: initialBookingId,
      cmp: extractCmp(routerQuery),
      query: routerQuery,
    });
    setOrderData(updatedOrderData);
  }, [initialBookingId, routerQuery]);

  // ── Polling effect ─────────────────────────────────────────────────

  useEffect(() => {
    const bookingId = initialBookingId;
    const pollingConfig = initialPollingConfig;

    // Initialize the ref with initial status if not already set
    if (initialStatusRef.current === null && initialPolicyStatus !== null) {
      initialStatusRef.current = initialPolicyStatus as string;
    }

    // Check if polling is enabled
    if (!pollingConfig?.isEnabled || !bookingId) {
      return;
    }

    // Prevent duplicate polling initialization for the same bookingId
    if (pollingInitializedRef.current === bookingId) {
      return;
    }

    // Clear any existing polling interval if bookingId changed
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Mark polling as initialized for this bookingId
    pollingInitializedRef.current = bookingId;
    isPollingRef.current = true;

    /** Stop polling and reset all refs */
    const stopPolling = () => {
      isPollingRef.current = false;
      pollingInitializedRef.current = null;
      pollingStartTimeRef.current = null;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const pollPolicyStatus = async () => {
      // Skip if polling was stopped
      if (!isPollingRef.current) {
        return;
      }

      // Set start time on first poll
      if (pollingStartTimeRef.current === null) {
        pollingStartTimeRef.current = Date.now();
      }

      try {
        const partner = getPartnerOnClientSide();
        const cmp = extractCmp(routerQuery);
        const result = await getPolicyStatus({
          partner,
          bookingId,
          cmp,
          query: routerQuery,
        });

        const currentPolicyStatus = result?.data?.policyStatus;

        if (!currentPolicyStatus) {
          return;
        }

        // Compare current status with initial status
        const statusChanged = initialStatusRef.current !== currentPolicyStatus;

        if (statusChanged) {
          // Status changed — stop polling and fetch updated order details
          stopPolling();

          try {
            const updatedOrderData = await getOrderDetailsV2({
              partner,
              bookingId,
              cmp,
              query: routerQuery,
            });

            // Smooth real-time update — no page reload
            setOrderData(updatedOrderData);
          } catch (error) {
            console.error("Error fetching updated order details:", error);
          }
        } else {
          // Check if duration has elapsed (only if status hasn't changed)
          if (
            pollingConfig?.durationMs &&
            pollingStartTimeRef.current !== null
          ) {
            const elapsed = Date.now() - pollingStartTimeRef.current;
            if (elapsed >= pollingConfig.durationMs) {
              stopPolling();
            }
          }
        }
      } catch (error) {
        console.error("Error polling policy status:", error);
        stopPolling();
      }
    };

    // Start polling immediately, then at interval
    pollPolicyStatus();
    pollingIntervalRef.current = setInterval(
      pollPolicyStatus,
      pollingConfig.intervalMs
    );

    // Cleanup on unmount or when dependencies change
    return stopPolling;
  }, [
    initialBookingId,
    initialPollingConfig,
    initialPolicyStatus,
    routerQuery,
  ]);

  return { orderData, setOrderData, refreshOrderDetails };
}
